import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, wordpressApi } from '../../api';
import { useQuery, useResource } from '../../hooks';
import { queries } from '../../queries';
import { ErrorNotice } from '../../components/ui';
import { SiteBadge } from '../../components/status/StatusPieces';
import { siteOf } from '../../components/status/statusModel';
import { ActionMenu, Breadcrumbs, ProjectFactsSkeleton, ProjectNavSkeleton, ProjectSkeleton } from '../../components/primitives';
import type { MenuAction } from '../../components/primitives';
import { SectionErrorBoundary } from '../../components/SectionErrorBoundary';
import { EnvironmentSelector } from '../../components/project/EnvironmentSelector';
import { ProjectNav } from '../../components/project/ProjectNav';
import { ProjectDeploymentBanner } from '../../components/project/ProjectDeploymentBanner';
import type { ProjectNavItem } from '../../components/project/ProjectNav';
import { NewDeploymentModal } from '../../components/NewDeploymentModal';
import {
  architectureLabel,
  deployedBranch,
  magentoVersion,
  setupProgress,
} from '../../components/project/projectHealth';
import { attentionFromHealth } from '../../components/project/operationalHealth';
import { useEnvironmentReadiness } from '../../components/EnvironmentReadiness';
import { ProjectContext } from './ProjectContext';
import { rememberedEnvironment, rememberEnvironment, resolveEnvironment } from './environmentSelection';
import type { ProjectContextValue } from './ProjectContext';
import { OverviewSection } from './OverviewSection';
import { DeploymentsSection } from './DeploymentsSection';
import { InfrastructureSection } from './InfrastructureSection';
import { RepositoriesSection } from './RepositoriesSection';
import { MonitoringSection } from './MonitoringSection';
import { RecoverySection } from './RecoverySection';
import { SettingsSection } from './SettingsSection';
import { SetupSection } from './SetupSection';
import { DistributionSection } from './DistributionSection';
import { ErpSection } from './ErpSection';
import { WordPressOverview } from './wordpress/WordPressOverview';
import { WordPressSiteSection } from './wordpress/WordPressSiteSection';
import { WordPressUpdatesSection } from './wordpress/WordPressUpdatesSection';
import { WordPressDeployModal } from './wordpress/WordPressDeployModal';
import { WordPressRecoverySection } from './wordpress/WordPressRecoverySection';
import { wordpressSetup, wordpressVersionFacts } from '../../components/project/wordpressHealth';

/**
 * ProjectShell is the project's persistent frame: the header that names the
 * project and its state, the environment context, the section navigation,
 * and the records every section reads. Sections render inside it, so the
 * header and the environment never flicker or reset when one is opened.
 */
export function ProjectShell() {
  const { projectId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // One token bumped by anything that changes the project or the server:
  // a deployment, a setup run, a component added or removed, a restore, a
  // new environment.
  const [reloadToken, setReloadToken] = useState(0);
  const refresh = useCallback(() => setReloadToken((value) => value + 1), []);

  const project = useQuery(queries.project(projectId), [reloadToken]);
  const servers = useQuery(queries.servers(), [reloadToken]);
  const gitConnection = useResource(() => api.getGitConnection(projectId), [projectId], undefined, { key: `project:${projectId}:git-connection` });
  // Which credential reads the repository, by the checkout's own rules
  // (CO-98): the one repository-access status the deploy dialog, setup and
  // Repositories show.
  const repositoryAccess = useResource(() => api.getRepositoryAccess(projectId).catch(() => null), [projectId, reloadToken], projectId, { key: `project:${projectId}:repository-access` });

  const environments = useMemo(() => project.data?.environments ?? [], [project.data]);
  const wantedEnvironment = searchParams.get('env');
  const environment = useMemo(
    () => resolveEnvironment(environments, wantedEnvironment ?? rememberedEnvironment(projectId)),
    [environments, wantedEnvironment, projectId],
  );
  const environmentId = environment?.id ?? null;

  // The URL is the source of truth for the environment: once one is
  // resolved, write it back so a copied link opens the same view.
  useEffect(() => {
    if (!environment) return;
    rememberEnvironment(projectId, environment.id);
    if (searchParams.get('env') === environment.slug) return;
    const next = new URLSearchParams(searchParams);
    next.set('env', environment.slug);
    setSearchParams(next, { replace: true });
  }, [environment, projectId, searchParams, setSearchParams]);

  const selectEnvironment = useCallback(
    (id: string) => {
      const chosen = environments.find((candidate) => candidate.id === id);
      if (!chosen) return;
      rememberEnvironment(projectId, chosen.id);
      // Switching environment drops any open deployment, run or group: they
      // belong to the environment that was selected.
      setSearchParams(new URLSearchParams({ env: chosen.slug }), { replace: true });
    },
    [environments, projectId, setSearchParams],
  );

  const sectionPath = useCallback(
    (section: string, params?: Record<string, string>) => {
      const query = new URLSearchParams();
      if (environment) query.set('env', environment.slug);
      for (const [key, value] of Object.entries(params ?? {})) query.set(key, value);
      const search = query.toString();
      return `/app/projects/${projectId}/${section}${search ? `?${search}` : ''}`;
    },
    [environment, projectId],
  );

  const environmentOverview = useQuery(queries.environmentOverview(projectId, environmentId), [reloadToken]);
  const components = useQuery(queries.components(projectId, environmentId), [reloadToken]);
  const deployments = useQuery(queries.deployments(projectId, environmentId), [reloadToken]);
  const recoveryPoints = useQuery(queries.recoveryPoints(projectId, environmentId), [reloadToken]);
  // The plan is a persisted record of the environment (CO-73): a 404 means
  // there is no plan yet; any other failure is shown, never read as "no
  // plan", which would silently hide an approved plan and its Run action.
  const plan = useQuery(queries.plan(projectId, environmentId), [reloadToken]);
  // A setup run ends on the server, not in this page. While one is pending
  // or running the plan is read again, and once it has ended everything it
  // changed is (a finished setup makes the environment deployable) —
  // wherever the person is in the project, not only on the run's own view.
  const planInFlight = plan.data?.lifecycle === 'run_pending' || plan.data?.lifecycle === 'running';
  const reloadPlan = plan.reload;
  useEffect(() => {
    if (!planInFlight) return;
    const timer = window.setInterval(reloadPlan, 5000);
    return () => window.clearInterval(timer);
  }, [planInFlight, reloadPlan]);
  const planWasInFlight = useRef(false);
  const planSettledKnown = Boolean(plan.data);
  useEffect(() => {
    if (planWasInFlight.current && !planInFlight && planSettledKnown) refresh();
    planWasInFlight.current = planInFlight;
  }, [planInFlight, planSettledKnown, refresh]);
  const applications = useQuery(queries.applications(projectId), [reloadToken]);
  const historyApplications = useQuery(queries.historyApplications(projectId), [reloadToken]);
  const incidents = useQuery(queries.incidents(projectId), [reloadToken]);
  // The environment's health, derived once on the server (CO-95): the
  // header word, the attention list and whether setup is complete are read
  // from it, never recomputed here.
  const environmentHealth = useQuery(queries.environmentHealth(projectId, environmentId), [reloadToken]);

  const isWordPress = project.data?.project.platform === 'wordpress';
  // The storefront record knows what a built release showed (a Hyvä theme
  // installed as a Composer package), which the plan's repository read
  // cannot; the plan is the fallback while there is no record.
  const platform = project.data ? project.data.project.platform || 'magento' : undefined;
  const storefrontRecord = useResource(
    () => (platform && platform !== 'wordpress' ? api.getMagentoStorefront(projectId).catch(() => null) : Promise.resolve(null)),
    [projectId, platform, reloadToken],
    projectId, { key: `project:${projectId}:storefront-record:${platform}` },
  );
  const recordedStorefront = storefrontRecord.data?.storefront?.effective;
  const storefrontType =
    recordedStorefront && recordedStorefront.source !== 'default' && recordedStorefront.type !== 'undetermined'
      ? recordedStorefront.type
      : plan.data?.repository_analysis?.storefront?.storefront;
  const wordpressSite = useResource(
    () =>
      isWordPress && environmentId
        ? wordpressApi
            .listSites(projectId)
            .then((res) => (res.sites ?? []).find((site) => site.environment_id === environmentId) ?? null)
        : Promise.resolve(null),
    [projectId, environmentId, isWordPress, reloadToken],
    environmentId, { key: `project:${projectId}:env:${environmentId}:wordpress-site:${isWordPress}` },
  );
  const errorGroups = useResource(
    () =>
      isWordPress && environmentId
        ? wordpressApi.listErrorGroups(projectId, environmentId).catch(() => ({ groups: [] }))
        : Promise.resolve({ groups: [] }),
    [projectId, environmentId, isWordPress, reloadToken],
    environmentId, { key: `project:${projectId}:env:${environmentId}:error-groups:${isWordPress}` },
  );

  // The readiness ledger inspects the server live; it renders the same
  // derivation with the host and server-state rows added.
  // Only the Overview (its summary line) and Infrastructure (the full ledger)
  // show it, so only they trigger the server inspection.
  const sectionSegment = location.pathname.split('/')[4] ?? '';
  const showsReadiness = !isWordPress && (sectionSegment === '' || sectionSegment === 'overview' || sectionSegment === 'infrastructure');
  const readiness = useEnvironmentReadiness(environmentId ?? '', sectionPath, reloadToken, showsReadiness, environmentHealth.data);

  const [deployPreselect, setDeployPreselect] = useState<string[] | null>(null);
  const openDeploy = useCallback((componentIds: string[]) => setDeployPreselect(componentIds), []);

  const applicationList = applications.data?.applications ?? [];
  const isHeadless = project.data?.project.architecture === 'headless';
  const placements = environmentOverview.data?.placements ?? [];
  const primaryPlaced = placements.some((placement) =>
    applicationList.some(
      (app) => app.id === placement.application_id && (app.type === 'magento_backend' || app.type === 'wordpress'),
    ),
  );
  const unplacedComponentIds = useMemo(() => {
    const placed = new Set(placements.map((placement) => placement.application_id));
    return applicationList.filter((app) => !placed.has(app.id)).map((app) => app.id);
  }, [placements, applicationList]);

  const deploymentList = deployments.data?.deployments ?? [];
  const magentoSetup = useMemo(
    () =>
      setupProgress({
        servers: servers.data?.servers ?? [],
        repositoryUrl: project.data?.project.repository_url ?? '',
        repositoryAccessConfigured: repositoryAccessReady(repositoryAccess.data, gitConnection.data?.configured),
        plan: plan.data,
        placementCount: placements.length,
      }),
    [servers.data, project.data, gitConnection.data, repositoryAccess.data, plan.data, placements.length],
  );
  const wpSetup = useMemo(
    () =>
      wordpressSetup({
        repositoryUrl: project.data?.project.repository_url ?? '',
        repositoryAccessConfigured: repositoryAccessReady(repositoryAccess.data, gitConnection.data?.configured),
        site: wordpressSite.data,
      }),
    [project.data, gitConnection.data, repositoryAccess.data, wordpressSite.data],
  );
  // The checklist is the wizard's; whether setup is complete is the
  // server's verdict, the same one the portfolio and the deploy dialog use.
  const setupChecklist = isWordPress ? wpSetup : magentoSetup;
  const serverSetup = environmentHealth.data?.setup;
  const setup = useMemo(
    () => (serverSetup ? { ...setupChecklist, complete: serverSetup.complete } : setupChecklist),
    [setupChecklist, serverSetup],
  );
  // Deployable once the primary application is placed and, for Magento,
  // its setup actually ran: approval alone places it (CO-73).
  const placedPrimary = primaryPlaced && (isWordPress || setup.complete);
  const attention = useMemo(
    () => attentionFromHealth(environmentHealth.data?.attention, sectionPath),
    [environmentHealth.data, sectionPath],
  );
  const health = environmentHealth.data?.level ?? 'unknown';

  const serverName = useCallback(
    (id: string) => servers.data?.servers?.find((server) => server.id === id)?.name ?? id.slice(0, 8),
    [servers.data],
  );
  const applicationNames = useCallback(
    (ids?: string[]) =>
      (ids ?? [])
        .map(
          (id) =>
            (historyApplications.data?.applications ?? applicationList).find((app) => app.id === id)?.name ??
            id.slice(0, 8),
        )
        .join(', '),
    [historyApplications.data, applicationList],
  );

  // A resource has been read once when it has data or failed; a reload
  // keeps the last data, so this never goes back to the skeleton.
  const settled = (resource: { data: unknown; error: unknown; loading: boolean }) =>
    resource.data !== null || resource.error !== null || !resource.loading;
  // The header's facts line and the section tabs wait for what decides
  // them, so a reload never shows "Unknown" or a Setup tab for a split
  // second and then jumps (CO-106).
  const factsReady =
    settled(environmentHealth) && settled(deployments) && (isWordPress ? settled(wordpressSite) : settled(plan));
  const navReady = settled(environmentHealth) && settled(deployments) && settled(incidents);

  if (project.loading && !project.data) return <ProjectSkeleton />;
  if (project.error) return <ErrorNotice error={project.error} />;
  if (!project.data) return null;

  const data = project.data;
  const basePath = `/app/projects/${projectId}`;
  const search = environment ? `?env=${encodeURIComponent(environment.slug)}` : '';
  const versionFacts = isWordPress ? wordpressVersionFacts(wordpressSite.data) : [magentoVersion(plan.data)].filter(Boolean);
  const branch = deployedBranch(deploymentList);

  const contextValue: ProjectContextValue = {
    projectId,
    project: data.project,
    environments,
    environment,
    selectEnvironment,
    sectionPath,
    servers,
    applications: applicationList,
    historyApplications: historyApplications.data?.applications ?? [],
    reloadApplications: () => {
      applications.reload();
      historyApplications.reload();
    },
    environmentOverview,
    components,
    deployments,
    recoveryPoints,
    environmentHealth,
    repositoryAccess,
    plan,
    gitConnection,
    isHeadless,
    isWordPress,
    wordpressSite,
    errorGroups,
    placedPrimary,
    setup,
    unplacedComponentIds,
    serverName,
    applicationNames,
    openDeploy,
    refresh,
  };

  // The operational sections are tabs; configuration lives under "More"
  // (CO-150). Monitoring counts open incidents: a number someone acts on.
  const openIncidents = incidents.data?.incidents?.length ?? 0;
  const navItems: ProjectNavItem[] =
    isWordPress
      ? [
          { id: 'overview', label: 'Overview' },
          { id: 'deployments', label: 'Deployments' },
          { id: 'site', label: 'Site' },
          { id: 'updates', label: 'Updates & security' },
          { id: 'monitoring', label: 'Monitoring', count: openIncidents },
          { id: 'recovery', label: 'Recovery' },
          { id: 'repositories', label: 'Repository' },
          { id: 'distribution', label: 'Product distribution' },
          { id: 'erp', label: 'ERP integrations' },
          { id: 'settings', label: 'Settings' },
        ]
      : [
          { id: 'overview', label: 'Overview' },
          // While setup is unfinished it is a section of its own (audit N4),
          // not only an entry in the "…" menu.
          ...(setup.complete ? [] : [{ id: 'setup', label: 'Setup', progress: `${setup.done}/${setup.total}` }]),
          { id: 'deployments', label: 'Deployments' },
          { id: 'infrastructure', label: 'Infrastructure' },
          { id: 'monitoring', label: 'Monitoring', count: openIncidents },
          { id: 'recovery', label: 'Recovery' },
          { id: 'repositories', label: 'Repositories & build' },
          { id: 'distribution', label: 'Product distribution' },
          { id: 'erp', label: 'ERP integrations' },
          { id: 'settings', label: 'Settings' },
        ];
  // The section the page shows, for the breadcrumb: the project name
  // links back to the Overview from every section (observation 7).
  const currentSectionId = location.pathname.slice(basePath.length + 1).split('/')[0] || 'overview';
  const currentSection = navItems.find((item) => item.id === currentSectionId);
  const overviewPath = sectionPath('overview');
  // A record opened inside a section (a deployment, a run, a preview): the
  // section's crumb leads back to its list, filters kept (CO-150).
  const detailKeys = ['deployment', 'run', 'preview', 'group'] as const;
  const openDetail = detailKeys.find((key) => searchParams.has(key));
  const listSearch = new URLSearchParams(searchParams);
  for (const key of [...detailKeys, 'intent', 'tab', 'step']) listSearch.delete(key);
  const detailCrumb: Record<(typeof detailKeys)[number], string> = { deployment: 'Deployment', run: 'Run on the server', preview: 'Database preview', group: 'Coordinated deployment' };
  const site = siteOf(environmentHealth.data);

  return (
    <ProjectContext.Provider value={contextValue}>
      <div className="project-area">
        <header className="project-top">
          <Breadcrumbs
            className="project-top__breadcrumb"
            items={[
              { label: 'Projects', to: '/app/projects' },
              ...(currentSection && currentSection.id !== 'overview'
                ? [
                    { label: data.project.name, to: overviewPath },
                    openDetail
                      ? { label: currentSection.label, to: `${basePath}/${currentSection.id}?${listSearch.toString()}` }
                      : { label: currentSection.label },
                    ...(openDetail ? [{ label: detailCrumb[openDetail] }] : []),
                  ]
                : [{ label: data.project.name }]),
            ]}
          />

          <div className="project-top__row">
            <div className="project-top__identity">
              <div className="project-top__name-row">
                <h1 className="project-top__title">
                  <Link to={overviewPath} className="project-top__title-link" title={`${data.project.name} overview`}>
                    {data.project.name}
                  </Link>
                </h1>
                <EnvironmentSelector environments={environments} selectedId={environment?.id ?? null} onSelect={selectEnvironment} />
              </div>
              {factsReady ? (
              <p className="project-top__facts">
                {/* The live site's own word, never the attention level (CO-150). */}
                <span className="project-top__site" data-testid="project-site-status">
                  <span className="project-top__site-label">Site</span>
                  <SiteBadge state={site.state} stale={site.stale} />
                </span>
                {versionFacts.map((fact) => (
                  <span key={fact} className="project-top__fact">
                    {fact}
                  </span>
                ))}
                {isWordPress ? null : (
                  <span className="project-top__fact">{architectureLabel(data.project.architecture, storefrontType)}</span>
                )}
                {branch ? (
                  <span className="project-top__fact">
                    <code>{branch}</code>
                  </span>
                ) : null}
              </p>
              ) : (
                <ProjectFactsSkeleton />
              )}
            </div>

            <ProjectDeploymentBanner
              projectId={projectId}
              deploymentPath={(deployment) => `${basePath}/deployments?env=${encodeURIComponent(deployment.environment_slug)}&deployment=${deployment.deployment_id}`}
            />

            <div className="project-top__actions">
              <button
                type="button"
                className="button button--primary"
                disabled={!environment || !placedPrimary}
                title={
                  placedPrimary
                    ? undefined
                    : isWordPress
                      ? 'Connect this environment’s WordPress site first.'
                      : 'Set up this environment on a server first.'
                }
                onClick={() => openDeploy([])}
                data-testid="project-deploy"
              >
                Deploy
              </button>
              <ProjectOverflowMenu
                sectionPath={sectionPath}
                environmentId={environmentId}
                setupComplete={setup.complete}
                isWordPress={isWordPress}
                sshEnvironment={environment?.deployment_mode === 'existing_atomic'}
              />
            </div>
          </div>

          {navReady ? (
          <ProjectNav
            basePath={basePath}
            search={search}
            items={navItems}
            currentId={currentSectionId}
          />
          ) : (
            <ProjectNavSkeleton />
          )}
        </header>

        <div className="project-body">
          <SectionErrorBoundary resetKey={location.pathname} label="This section">
          {isWordPress ? (
            <Routes>
              <Route path="overview" element={<WordPressOverview health={health} attention={attention} setup={setup} />} />
              <Route path="deployments" element={<DeploymentsSection />} />
              <Route path="site" element={<WordPressSiteSection />} />
              <Route path="updates" element={<WordPressUpdatesSection />} />
              <Route path="repositories" element={<RepositoriesSection />} />
              <Route path="monitoring" element={<MonitoringSection />} />
              <Route path="recovery" element={<WordPressRecoverySection />} />
              <Route path="distribution" element={<DistributionSection />} />
              <Route path="erp" element={<ErpSection />} />
              <Route path="settings" element={<SettingsSection />} />
              {/* Magento-only sections resolve to where a WordPress project keeps the same thing. */}
              <Route path="setup" element={<Navigate to={`${basePath}/site${location.search}`} replace />} />
              <Route path="infrastructure" element={<Navigate to={`${basePath}/site${location.search}`} replace />} />
              <Route path="*" element={<Navigate to={`${basePath}/overview${location.search}`} replace />} />
            </Routes>
          ) : (
            <Routes>
              <Route
                path="overview"
                element={
                  <OverviewSection
                    health={health}
                    environmentHealth={environmentHealth}
                    attention={attention}
                    setup={setup}
                    readiness={readiness}
                  />
                }
              />
              <Route path="deployments" element={<DeploymentsSection />} />
              <Route path="infrastructure" element={<InfrastructureSection readiness={readiness} />} />
              <Route path="repositories" element={<RepositoriesSection />} />
              <Route path="monitoring" element={<MonitoringSection />} />
              <Route path="distribution" element={<DistributionSection />} />
              <Route path="erp" element={<ErpSection />} />
              <Route path="recovery" element={<RecoverySection />} />
              <Route path="settings" element={<SettingsSection />} />
              <Route path="setup" element={<SetupSection setup={setup} />} />
              <Route path="*" element={<Navigate to={`${basePath}/overview${location.search}`} replace />} />
            </Routes>
          )}
          </SectionErrorBoundary>
        </div>
      </div>

      {deployPreselect !== null && environment && isWordPress ? (
        <WordPressDeployModal
          projectId={projectId}
          environment={environment}
          application={applicationList.find((app) => app.type === 'wordpress') ?? null}
          onClose={() => setDeployPreselect(null)}
          onCreated={(target) => {
            setDeployPreselect(null);
            refresh();
            navigate(sectionPath('deployments', target.deployment ? { deployment: target.deployment } : { group: target.group }));
          }}
        />
      ) : null}
      {deployPreselect !== null && environment && !isWordPress ? (
        <NewDeploymentModal
          projectId={projectId}
          environment={environment}
          isHeadless={isHeadless}
          applications={applicationList}
          initialComponentIds={deployPreselect}
          unplacedComponentIds={unplacedComponentIds}
          onClose={() => setDeployPreselect(null)}
          onCreated={(deploymentId) => {
            setDeployPreselect(null);
            refresh();
            navigate(sectionPath('deployments', { deployment: deploymentId }));
          }}
          health={environmentHealth.data}
          repositoryAccess={repositoryAccess.data}
          sectionPath={sectionPath}
          onGroupCreated={(groupId) => {
            setDeployPreselect(null);
            refresh();
            navigate(sectionPath('deployments', { group: groupId }));
          }}
          onPreviewCreated={(previewId) => {
            setDeployPreselect(null);
            navigate(sectionPath('deployments', { preview: previewId, view: 'previews' }));
          }}
        />
      ) : null}
    </ProjectContext.Provider>
  );
}

/**
 * repositoryAccessReady: a credential reads the repository — the project's
 * token or the organization's GitHub App (UX-13: "Repository access
 * configured" stayed undone for a project read through the App).
 */
function repositoryAccessReady(access: { source: string } | null | undefined, projectTokenConfigured: boolean | undefined): boolean {
  if (access) return access.source === 'project_connection' || access.source === 'github_app';
  return projectTokenConfigured ?? false;
}

/**
 * ProjectOverflowMenu keeps the low-frequency destinations out of the
 * header's action row without hiding them: a real menu button, operable
 * with the keyboard and dismissed with Escape.
 */
function ProjectOverflowMenu({
  sectionPath,
  environmentId,
  setupComplete,
  isWordPress,
  sshEnvironment,
}: {
  sectionPath: (section: string, params?: Record<string, string>) => string;
  environmentId: string | null;
  setupComplete: boolean;
  isWordPress: boolean;
  sshEnvironment: boolean;
}) {
  const items: MenuAction[] = isWordPress
    ? [{ label: setupComplete ? 'WordPress site' : 'Connect the WordPress site', to: sectionPath('site') }]
    : [
        { label: setupComplete ? 'Server setup record' : 'Continue server setup', to: sectionPath('setup') },
        { label: 'Back up and restore', to: sectionPath('recovery') },
      ];
  // Only an SSH environment still has a page of its own (audit N5).
  if (environmentId && sshEnvironment) items.push({ label: 'Environment settings', to: `/app/environments/${environmentId}` });
  items.push({ label: 'Project settings', to: sectionPath('settings') });
  return <ActionMenu label="More project actions" items={items} testId="project-overflow" />;
}

/**
 * ProjectIndexRedirect keeps every bookmark of the old single-page project
 * URL working: it resolves to the Overview, carrying any environment the
 * link already named.
 */
export function ProjectIndexRedirect() {
  const { projectId } = useParams();
  const location = useLocation();
  return <Navigate to={`/app/projects/${projectId}/overview${location.search}`} replace />;
}
